import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import productionWorkerService from "../../../../services/productionWorkerService";
import storageWorkerService from "../../../../services/storageWorkerService";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { useSimulationData } from "../../../../hooks/useSimulationData";
import { buildDemandChargePayload } from "../../utils/demandCharge";
import {
  buildTwinStrokeData,
  isDrivingPoint,
  readTwinStrokePoint,
  twinStrokeValue,
  twinStrokeValues,
} from "../../utils/twinStrokeSeries";
import {
  LOOSE,
  makeStorageFlowReader,
  readColumn,
} from "../../utils/columnLookup";
import {
  startColumnSum,
  addColumnValue,
  finishColumnSum,
  sumBackendColumns,
  hasBackendTotalsColumn,
  sumBackendColumn,
} from "../../utils/columnSums";
import { calculateConsumptionTotals } from "../../utils/consumptionTotals";
import {
  getDeviceColor,
  BESS_BASE_COLOR,
  SOLAR_BASE_COLOR,
  GENERATOR_BASE_COLOR,
  TRANSFER_LINE_COLOR,
  CONSUMPTION_LINE_COLOR,
  STORAGE_SUMMARY_LINE_COLOR,
  PRODUCTION_SUMMARY_LINE_COLOR,
} from "../../utils/deviceColors";
import { parseSimulationDate } from "../../utils/simulationDate";
import { formatWithCommas } from "../../utils/formatNumber";
import {
  buildDataZoom,
  formatDate,
  getXAxisTitle,
  HOURLY_BUCKET_STEP,
} from "../../utils/chartOptions";

const toTestId = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const STORAGE_SUMMARY_SERIES_NAME = "Storage Summary";
const PRODUCTION_SUMMARY_SERIES_NAME = "Production Summary";

/* Same key the stat cards build in public/workers/metrics.worker.js, and the
 * same one StorageDashboard/StorageChart.jsx reads. Energy that left the EV
 * through the wheels rather than the plug: absent from results produced before
 * the sim's two-exit accounting change, and from BESS devices, which have no
 * wheels exit — both read as 0 here. */
const drivingKeyFor = (deviceName) => `${deviceName} EV Driving Energy (kWh)`;
const getEVDriving = (entry, name) => Number(entry[drivingKeyFor(name)]) || 0;

/* The dashed twin needs an id of its own: ECharts matches series across a
 * replaceMerge by id, and the two strokes deliberately share a name so the
 * legend treats them as one device. */
const drivingSeriesIdFor = (deviceName) => `${deviceName}::driving`;
/* The zero-width verticals that join a device's two strokes at each hand-over.
 * Their own series because they are geometry rather than readings - see the
 * join note in utils/twinStrokeSeries.js. */
const connectorSeriesIdFor = (deviceName) => `${deviceName}::connect`;
const drivingConnectorSeriesIdFor = (deviceName) =>
  `${deviceName}::connect-driving`;
/* The series that are ON when the page opens. Everything else - every device,
 * and the tariff pair - is OFF until the user asks for it. */
const SUMMARY_SERIES_NAMES = new Set([
  "Transfer",
  "Consumption",
  STORAGE_SUMMARY_SERIES_NAME,
  PRODUCTION_SUMMARY_SERIES_NAME,
]);

/* THE ONE PLACE that answers "is this series showing?", and the only safe way
 * to read the visibility map.
 *
 * `visibleSeries` is state keyed by legend item, and `legendItems` is built
 * from deviceNames/proddeviceNames, which arrive on a LATER tick than the rows.
 * Every derived value gains the devices in the render the lists land; state
 * cannot, because it is written by an effect one commit later. So there is
 * always a window in which the map holds NO ENTRY for a device.
 *
 * Read raw, that window painted them. Each site tested `map[name] !== false`,
 * and an absent device is `undefined !== false` -> visible, so all of them
 * appeared for one commit and were then switched off as the map caught up.
 * That is the flash the product owner reported: "still popping up first as
 * individual devices, then switching over".
 *
 * The default therefore has to depend on WHICH series is missing - a blanket
 * `=== true` would blank the summary lines until the map filled, which is a
 * worse flash, not a fix. SUMMARY_SERIES_NAMES already draws exactly that line.
 *
 * `??` falls back only on `undefined`, so an explicit `false` still wins: a
 * legend click or a summary-card toggle writes a real boolean and this returns
 * it unchanged. A summary line switched off stays off.
 *
 * Takes the map rather than closing over it so the same rule serves the
 * `setVisibleSeries` updaters, which see `prev` and not the rendered state. */
const resolveSeriesVisibility = (visibility, seriesName) =>
  visibility[seriesName] ?? SUMMARY_SERIES_NAMES.has(seriesName);

const OVERLAY_HIDDEN_LEGEND_SERIES = new Set([
  "Transfer",
  "Consumption",
  STORAGE_SUMMARY_SERIES_NAME,
  PRODUCTION_SUMMARY_SERIES_NAME,
]);
const SUMMARY_CARD_TO_SERIES_NAME = {
  Transfer: "Transfer",
  Production: PRODUCTION_SUMMARY_SERIES_NAME,
  Consumption: "Consumption",
  Storage: STORAGE_SUMMARY_SERIES_NAME,
};
const SERIES_NAME_TO_SUMMARY_CARD = Object.fromEntries(
  Object.entries(SUMMARY_CARD_TO_SERIES_NAME).map(([cardTitle, seriesName]) => [
    seriesName,
    cardTitle,
  ]),
);
const CHART_VALUE_DECIMALS = 2;
const CHART_LINE_WIDTH = 2;
const HIGHLIGHTED_SUMMARY_LINE_WIDTH = 4;
const BACKGROUND_DEVICE_LINE_ALPHA = 0.74;
const DIMMED_SUMMARY_LINE_ALPHA = 0.42;
const DIMMED_DEVICE_LINE_ALPHA = 0.38;

// Backend Totals super-header columns produced by the updated simulation
// endpoint. The two-row CSV header parser flattens each into "Totals <name>".
const TOTALS_COLUMNS = {
  productionEnergy: "Totals Total Production (kWh)",
  productionCost: "Totals Production Cost ($)",
  transferEnergy: "Totals Total Transfer (kWh)",
  transferCost: "Totals Transfer Cost ($)",

  /* ── Per-flow money, for the cards' Value column ────────────────────────
   *
   * The four card TOTALS above are the engine's own per-card figures. These are
   * the engine's decomposition of those totals across the flows the card shows
   * as rows -- the money that belongs on the Import row rather than on the
   * Transfer card as a whole.
   *
   * They are summed in the SAME loops that already produce the totals, never in
   * a second pass: the per-row cost of these loops has already been measured and
   * reduced once, and re-walking 8,784 rows to read a neighbouring column of the
   * row we are already holding would hand that back for nothing.
   *
   * Read and summed, never derived -- see the note on engine math in
   * cardFlowRows.js. The field each lands on is named there; the registry in
   * that file is what a card looks a figure up by. */
  transferImportCost: "Totals Transfer Import Cost ($)",
  transferExportRevenue: "Totals Transfer Export Revenue ($)",

  /* Only the all-devices Production effect reads this. Every other card sums the
   * per-device figures instead, so that a card showing a subset of devices shows
   * that subset's money -- that effect is the one case with no per-device list to
   * sum, because it short-circuits the worker entirely when the Totals columns
   * are present. */
  productionGenerationValue: "Totals Production Generation Value ($)",

  /* The three CONSUMPTION columns are not here: they moved to
   * utils/consumptionTotals.js with the function that reads them, which
   * ConsumptionChart now shares. */
};

/* The column-sum helpers these effects use -- startColumnSum / addColumnValue /
 * finishColumnSum / sumBackendColumns / hasBackendTotalsColumn /
 * sumBackendColumn -- moved to utils/columnSums.js when the Consumption card's
 * totals had to be shared with ConsumptionChart. The rule they encode, and why
 * a missing column must not sum to 0, is written down there. */

/* Sum one money field across the per-device metrics a worker returned.
 *
 * Absence is per device and must survive aggregation: a worker reports null for
 * a device whose column the result file does not carry. If NO device reported a
 * figure the summary has nothing to show and stays absent (em-dash); if any did,
 * the sum is real. Devices reporting null contribute nothing rather than 0.
 *
 * This is why the summary rows are summed from the per-device list rather than
 * read from the matching `Totals` column: these cards honour device selection,
 * so a card showing three of eight devices must show those three devices' money.
 * Verified equal on husky-2040-prod with everything visible -- the seven storage
 * devices' charge costs sum to -498,960.65, exactly
 * `Totals Storage Charge Cost ($)`. */
const sumDeviceField = (metrics, field) => {
  let total = 0;
  let seen = false;
  for (const item of metrics) {
    const raw = item?.[field];
    if (raw === undefined || raw === null || raw === "") continue;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) continue;
    seen = true;
    total += numeric;
  }
  return seen ? total.toFixed(2) : null;
};

/** Largest per-device figure, or null when no device reported one. */
const maxDeviceField = (metrics, field) => {
  let best = null;
  for (const item of metrics) {
    const raw = item?.[field];
    if (raw === undefined || raw === null || raw === "") continue;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) continue;
    if (best === null || numeric > best) best = numeric;
  }
  return best === null ? null : best.toFixed(2);
};

/* The Storage summary card's per-flow figures, aggregated from the per-device
 * metrics the worker already returned.
 *
 * Both storage effects publish this, so the two surfaces cannot drift.
 *
 * ── The Consumed row was blank for a reason that had nothing to do with data ──
 * metrics.worker.js has computed `consumedMax`, `consumedTotal` and
 * `operatingCost` per device for as long as the two-exit accounting has existed,
 * and the per-device Storage cards show them. The SUMMARY object simply never
 * carried them: the reduce blocks aggregated totalEnergy, chargeTotal,
 * dischargeTotal, value and avgSOC and stopped there. So the summary card asked
 * for `summary.operatingCost`, got undefined, and rendered an em-dash across the
 * whole Consumed row -- on a card whose TOTAL was showing a real figure the
 * entire time. Same failure as the money columns, one layer further in: the
 * figure existed and nothing read it.
 *
 * consumedMax is a MAX across devices, not a sum -- it is the largest single
 * device peak, matching how minPower/maxPower are derived for this card. Summing
 * peaks would invent a coincident maximum that never happened.
 *
 * Charged and Discharge stay separate figures and are never added together; the
 * card total remains the engine's own `Storage Cost ($)`. */
const storageSummaryFlowMoney = (metrics) => ({
  chargedCost: sumDeviceField(metrics, "chargedCost"),
  dischargeRevenue: sumDeviceField(metrics, "dischargeRevenue"),
  operatingCost: sumDeviceField(metrics, "operatingCost"),
  /* True when ANY selected device reported driving energy, exactly as
   * `hasLosses` below works, and for the same reason: it is what lets the
   * Driving row distinguish "nothing here recorded driving" -- a battery-only
   * selection, or a run predating the column -- from "the vehicles drove 0".
   * Without it the summary card showed a bare daggered Driving row with no way
   * to tell those apart, while the Losses row beside it drew the distinction. */
  hasConsumed: metrics.some((item) => item?.hasConsumed),
  consumedTotal: sumDeviceField(metrics, "consumedTotal"),
  consumedMax: maxDeviceField(metrics, "consumedMax"),
  /* The site's Losses row.
   *
   * The engine writes site-level forms of both columns in its `Totals` group,
   * and they are NOT read here, for the reason the production card records one
   * effect down: this card honours DEVICE SELECTION. A Totals column keeps
   * reporting the whole site after the user hides half of it, which is a wrong
   * number that looks entirely right. Summing the per-device figures the worker
   * already returned is the only form that tracks the selection.
   *
   * lossesMax is a MAX across devices, not a sum -- the largest single device's
   * peak loss, matching consumedMax and minPower/maxPower above it. Summing
   * peaks would invent a coincident maximum that never happened.
   *
   * `hasLosses` is true when ANY selected device reported the column, which is
   * what lets the row distinguish "no device recorded this" (em-dash) from "the
   * devices recorded 0.00" (a measured zero). sumDeviceField already returns
   * null when nothing reported, so the two agree. */
  hasLosses: metrics.some((item) => item?.hasLosses),
  lossesTotal: sumDeviceField(metrics, "lossesTotal"),
  lossesMax: maxDeviceField(metrics, "lossesMax"),
  lossCost: sumDeviceField(metrics, "lossCost"),
});

const roundChartValue = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Number(numericValue.toFixed(CHART_VALUE_DECIMALS));
};

const formatChartValue = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return numericValue.toFixed(CHART_VALUE_DECIMALS);
};

const withAlpha = (color, alpha) => {
  if (typeof color !== "string") return color;

  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => `${char}${char}`)
        .join("");
    }
    if (hex.length === 8) {
      hex = hex.slice(0, 6);
    }
    if (hex.length !== 6) return color;

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  if (color.startsWith("rgba(")) {
    const parts = color.slice(5, -1).split(",").map((part) => part.trim());
    if (parts.length !== 4) return color;
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }

  if (color.startsWith("rgb(")) {
    const parts = color.slice(4, -1).split(",").map((part) => part.trim());
    if (parts.length !== 3) return color;
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }

  return color;
};

export default function OverlayChart({
  csvData,
  deviceNames,
  proddeviceNames,
  bessMaxCapacity,
  evMaxCapacity,
  onLoadingStateChange,
  hoveredSummaryCardTitle,
  summaryCardVisibility,
  summaryCardToggleRequest,
  onSummaryCardVisibilityChange,
  summaryHeader,
  dailyDemand,
  hasDemandCharge,
}) {
  const { dailyData, allTimeData, isLoading, error, refetchData } = useSimulationData(csvData);
  const {
    setExportedTransferData,
    setOverallExportedTransferData,
    setExportedData,
    setOverallExportedData,
    setExportedProductionData,
    setExportedProductionSummaryData,
    setOverallExportedProductionSummaryData,
    setExportedStorageData,
    setExportedStorageSummaryData,
    setOverallExportedStorageSummaryData,
    setExportedDemandChargeData,
    setOverallExportedDemandChargeData,
  } = useExportedData();
  const [view, setView] = useState("yearly");
  const [selDate, setSelDate] = useState("");
  const [selMonth, setSelMonth] = useState("");
  const [selYear, setSelYear] = useState("");
  const [chartData, setChartData] = useState([]);
  const [hoveredLegendSeriesName, setHoveredLegendSeriesName] = useState("");
  const [lastVisiblePowerAxisExtents, setLastVisiblePowerAxisExtents] =
    useState(null);
  const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });
  const latestProductionRequestRef = useRef(0);
  const latestStorageRequestRef = useRef(0);
  const latestOverallProductionRequestRef = useRef(0);
  const latestOverallStorageRequestRef = useRef(0);
  const chartRef = useRef(null);
  // Series names as of the last setOption, to tell a series-set change (which
  // invalidates a captured tooltip payload) from a data-only update.
  const previousSeriesNamesRef = useRef(null);
  const chartWrapperRef = useRef(null);
  
  const pricingSeriesNames = useMemo(
    () => ["Sell ($/kWh)", "Buy ($/kWh)"],
    [],
  );

  const legendItems = useMemo(
    () => [
      "Transfer",
      "Consumption",
      ...((deviceNames?.BESS?.length || deviceNames?.EV?.length)
        ? [STORAGE_SUMMARY_SERIES_NAME]
        : []),
      ...(deviceNames?.BESS || []),
      ...(deviceNames?.EV || []),
      ...((proddeviceNames?.Solar?.length || proddeviceNames?.Generator?.length)
        ? [PRODUCTION_SUMMARY_SERIES_NAME]
        : []),
      ...(proddeviceNames?.Solar || []),
      ...(proddeviceNames?.Generator || []),
      "Pricing",
    ],
    [deviceNames, proddeviceNames],
  );
  const displayedLegendItems = useMemo(
    () => legendItems.filter((item) => !OVERLAY_HIDDEN_LEGEND_SERIES.has(item)),
    [legendItems],
  );
  /* Only ever the first render's legend items - the device lists are still
   * empty then. Kept because it saves a state write on mount, NOT because it
   * establishes the defaults: resolveSeriesVisibility does that, and this
   * defers to it so the rule lives in one place. */
  const [visibleSeries, setVisibleSeries] = useState(() =>
    Object.fromEntries(
      legendItems.map((item) => [item, resolveSeriesVisibility({}, item)]),
    ),
  );
  /* Every visibility test on this chart goes through here. One left reading the
   * map raw reintroduces the flash for whatever it controls. */
  const isSeriesVisible = useCallback(
    (seriesName) => resolveSeriesVisibility(visibleSeries, seriesName),
    [visibleSeries],
  );
  const isStorageSummaryVisible = isSeriesVisible(STORAGE_SUMMARY_SERIES_NAME);
  const isProductionSummaryVisible = isSeriesVisible(
    PRODUCTION_SUMMARY_SERIES_NAME,
  );
  const activeStorageDeviceNames = useMemo(
    () => ({
      BESS: (deviceNames?.BESS || []).filter(isSeriesVisible),
      EV: (deviceNames?.EV || []).filter(isSeriesVisible),
    }),
    [deviceNames, isSeriesVisible],
  );
  const activeProductionDeviceNames = useMemo(
    () => ({
      Solar: (proddeviceNames?.Solar || []).filter(isSeriesVisible),
      Generator: (proddeviceNames?.Generator || []).filter(isSeriesVisible),
    }),
    [proddeviceNames, isSeriesVisible],
  );
  const storageCategorySeriesNames = useMemo(
    () => [
      STORAGE_SUMMARY_SERIES_NAME,
      ...(deviceNames?.BESS || []),
      ...(deviceNames?.EV || []),
    ],
    [deviceNames],
  );
  const productionCategorySeriesNames = useMemo(
    () => [
      PRODUCTION_SUMMARY_SERIES_NAME,
      ...(proddeviceNames?.Solar || []),
      ...(proddeviceNames?.Generator || []),
    ],
    [proddeviceNames],
  );
  const getSummaryCardTitleForSeries = useCallback(
    (seriesName) => {
      if (seriesName === "Transfer") return "Transfer";
      if (seriesName === "Consumption") return "Consumption";
      if (storageCategorySeriesNames.includes(seriesName)) return "Storage";
      if (productionCategorySeriesNames.includes(seriesName)) return "Production";
      return null;
    },
    [productionCategorySeriesNames, storageCategorySeriesNames],
  );
  const highlightedSummarySeriesName = useMemo(() => {
    switch (hoveredSummaryCardTitle) {
      case "Transfer":
        return "Transfer";
      case "Consumption":
        return "Consumption";
      case "Storage":
        return STORAGE_SUMMARY_SERIES_NAME;
      case "Production":
        return PRODUCTION_SUMMARY_SERIES_NAME;
      default:
        return "";
    }
  }, [hoveredSummaryCardTitle]);
  const highlightedSeriesName =
    hoveredLegendSeriesName || highlightedSummarySeriesName;
  const topLevelSummaryVisibility = useMemo(
    () => ({
      Transfer: isSeriesVisible("Transfer"),
      Production: productionCategorySeriesNames.some(isSeriesVisible),
      Consumption: isSeriesVisible("Consumption"),
      Storage: storageCategorySeriesNames.some(isSeriesVisible),
    }),
    [productionCategorySeriesNames, storageCategorySeriesNames, isSeriesVisible],
  );

  const updateZoomRange = useCallback((nextZoomRange) => {
    if (
      !nextZoomRange ||
      !Number.isFinite(nextZoomRange.start) ||
      !Number.isFinite(nextZoomRange.end)
    ) {
      return;
    }

    setZoomRange((previousZoomRange) => {
      if (
        Math.abs(previousZoomRange.start - nextZoomRange.start) < 0.001 &&
        Math.abs(previousZoomRange.end - nextZoomRange.end) < 0.001
      ) {
        return previousZoomRange;
      }

      return nextZoomRange;
    });
  }, []);

  const readCurrentZoomRange = useCallback(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    const option = chartInstance?.getOption?.();
    const activeDataZoom = Array.isArray(option?.dataZoom)
      ? option.dataZoom[0]
      : null;
    const start = Number(activeDataZoom?.start);
    const end = Number(activeDataZoom?.end);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return null;
    }

    return { start, end };
  }, []);

  const handleDataZoom = useCallback(() => {
    updateZoomRange(readCurrentZoomRange());
  }, [readCurrentZoomRange, updateZoomRange]);

  useEffect(() => {
    setZoomRange({ start: 0, end: 100 });
  }, [view, selDate, selMonth, selYear]);

  useEffect(() => {
    const wrapperElement = chartWrapperRef.current;
    if (!wrapperElement) return undefined;

    wrapperElement.__setSummaryOverlayZoomRange = (nextZoomRange) => {
      const start = Number(nextZoomRange?.start);
      const end = Number(nextZoomRange?.end);

      if (!Number.isFinite(start) || !Number.isFinite(end)) return;

      updateZoomRange({ start, end });
    };

    const handleSetZoomEvent = (event) => {
      const start = Number(event.detail?.start);
      const end = Number(event.detail?.end);

      if (!Number.isFinite(start) || !Number.isFinite(end)) return;

      updateZoomRange({ start, end });
    };

    wrapperElement.addEventListener(
      "summary-overlay-set-zoom",
      handleSetZoomEvent,
    );

    return () => {
      delete wrapperElement.__setSummaryOverlayZoomRange;
      wrapperElement.removeEventListener(
        "summary-overlay-set-zoom",
        handleSetZoomEvent,
      );
    };
  }, [updateZoomRange, chartData.length]);

  useEffect(() => {
    const wrapper = chartWrapperRef.current;
    if (!wrapper) return undefined;

    const ro = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance?.()?.resize();
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []);

  /* Materialise an entry for every legend item, and drop entries for items that
   * are gone. This no longer decides what anything LOOKS like - the resolver
   * answers that for an absent name, so nothing waits on this effect and the
   * flash is gone whether it has run or not. It is kept because two things
   * still need the map to be complete rather than sparse:
   *   - the summary-card toggle below, which skips a series it does not already
   *     hold (`if (!(seriesName in previousVisibility)) return`), so a card
   *     could not switch its devices off if they were never written; and
   *   - dropping stale entries, so a device that disappears when another
   *     simulation loads does not silently carry its old toggle back if a
   *     device of the same name returns.
   * The default it fills in is the resolver's, so the two cannot disagree. */
  useEffect(() => {
    setVisibleSeries((previousVisibility) => {
      const nextVisibility = {};
      let didChange = false;

      legendItems.forEach((item) => {
        const resolvedVisibility = resolveSeriesVisibility(
          previousVisibility,
          item,
        );

        nextVisibility[item] = resolvedVisibility;

        if (previousVisibility[item] !== resolvedVisibility) {
          didChange = true;
        }
      });

      if (Object.keys(previousVisibility).length !== legendItems.length) {
        didChange = true;
      }

      return didChange ? nextVisibility : previousVisibility;
    });
  }, [legendItems]);

  useEffect(() => {
    if (!summaryCardToggleRequest?.title) return;

    setVisibleSeries((previousVisibility) => {
      let didChange = false;
      const nextVisibility = { ...previousVisibility };
      const seriesNames =
        summaryCardToggleRequest.title === "Production"
          ? productionCategorySeriesNames
          : summaryCardToggleRequest.title === "Storage"
            ? storageCategorySeriesNames
            : [SUMMARY_CARD_TO_SERIES_NAME[summaryCardToggleRequest.title]].filter(
                Boolean,
              );

      seriesNames.forEach((seriesName) => {
        if (!(seriesName in previousVisibility)) return;

        const shouldBeVisible = summaryCardToggleRequest.visible !== false;
        if (previousVisibility[seriesName] === shouldBeVisible) return;

        nextVisibility[seriesName] = shouldBeVisible;
          didChange = true;
      });

      return didChange ? nextVisibility : previousVisibility;
    });
  }, [
    productionCategorySeriesNames,
    storageCategorySeriesNames,
    summaryCardToggleRequest,
  ]);

  useEffect(() => {
    onSummaryCardVisibilityChange?.(topLevelSummaryVisibility);
  }, [onSummaryCardVisibilityChange, topLevelSummaryVisibility]);

  const selectedStorageSummaryDeviceNames = useMemo(() => {
    const hasActiveStorageDevices =
      activeStorageDeviceNames.BESS.length > 0 ||
      activeStorageDeviceNames.EV.length > 0;

    if (hasActiveStorageDevices) {
      return activeStorageDeviceNames;
    }

    if (isStorageSummaryVisible) {
      return {
        BESS: deviceNames?.BESS || [],
        EV: deviceNames?.EV || [],
      };
    }

    return { BESS: [], EV: [] };
  }, [activeStorageDeviceNames, deviceNames, isStorageSummaryVisible]);
  const allStorageDeviceNames = useMemo(
    () => ({
      BESS: deviceNames?.BESS || [],
      EV: deviceNames?.EV || [],
    }),
    [deviceNames],
  );
  const selectedProductionSummaryDeviceNames = useMemo(() => {
    const hasActiveProductionDevices =
      activeProductionDeviceNames.Solar.length > 0 ||
      activeProductionDeviceNames.Generator.length > 0;

    if (hasActiveProductionDevices) {
      return activeProductionDeviceNames;
    }

    if (isProductionSummaryVisible) {
      return {
        Solar: proddeviceNames?.Solar || [],
        Generator: proddeviceNames?.Generator || [],
      };
    }

    return { Solar: [], Generator: [] };
  }, [
    activeProductionDeviceNames,
    proddeviceNames,
    isProductionSummaryVisible,
  ]);
  const allProductionDeviceNames = useMemo(
    () => ({
      Solar: proddeviceNames?.Solar || [],
      Generator: proddeviceNames?.Generator || [],
    }),
    [proddeviceNames],
  );
  // True when no individual production device is currently toggled off. Used
  // to decide whether the sticky production summary card can read directly
  // from the backend `Totals` columns or must fall back to per-device worker
  // sums (so it shrinks when a single device is hidden).
  const areAllProductionDevicesVisible = useMemo(
    () =>
      selectedProductionSummaryDeviceNames.Solar.length ===
        allProductionDeviceNames.Solar.length &&
      selectedProductionSummaryDeviceNames.Generator.length ===
        allProductionDeviceNames.Generator.length,
    [selectedProductionSummaryDeviceNames, allProductionDeviceNames],
  );
  const selectedPeriodType = useMemo(() => {
    if (view === "daily") return "Day";
    if (view === "monthly") return "Month";
    if (view === "yearly") return "Year";
    if (view === "all time view") return "All Time View";
    return "";
  }, [view]);

  const selectedPeriod = useMemo(() => {
    if (view === "daily") {
      if (!selDate) return "";
      const [year, month, day] = selDate.split("-");
      return `${month}/${day}/${year}`;
    }

    if (view === "monthly") {
      if (!selMonth) return "";
      const [year, month] = selMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${year}`;
    }

    if (view === "yearly") return selYear;
    if (view === "all time view") return "";
    return "";
  }, [view, selDate, selMonth, selYear]);

  const selectedPeriodRef = useRef(selectedPeriod);
  const selectedPeriodTypeRef = useRef(selectedPeriodType);
  selectedPeriodRef.current = selectedPeriod;
  selectedPeriodTypeRef.current = selectedPeriodType;
  const zeroProductionSummaryData = useMemo(
    () => ({
      totalProduction: "0.00",
      maxPower: "0.00",
      totalCost: "0.00",
      /* No production device, so no generation value -- a real zero, which
       * is what lets the site-net line leave fuel off rather than show an
       * em-dash for a generator that does not exist. */
      generatedValue: "0.00",
      view: selectedPeriodType,
      selectedPeriod,
    }),
    [selectedPeriodType, selectedPeriod],
  );
  const zeroStorageSummaryData = useMemo(
    () => ({
      totalEnergy: "0.00",
      chargeTotal: "0.00",
      dischargeTotal: "0.00",
      minPower: "0.00",
      maxPower: "0.00",
      value: "0.00",
      avgSOC: "0.00",
      /* Likewise real zeros: nothing to charge, nothing discharged, so the
       * Held term on the site-net line is 0 rather than unknown. */
      chargedCost: "0.00",
      dischargeRevenue: "0.00",
      view: selectedPeriodType,
      selectedPeriod,
    }),
    [selectedPeriodType, selectedPeriod],
  );

  const xAxisTitle = getXAxisTitle(view);




  

 
   const calculateTransferMetrics = (data) => {
  if (!data || data.length === 0) {
    return {
      minPower: "0.00",
      maxPower: "0.00",
      totalImport: "0.00",
      totalExport: "0.00",
      value: "0.00",
    };
  }

  // Prefer the backend `Totals` columns at the dataset level so we never
  // fall back to legacy columns row-by-row when the backend signal exists.
  const useBackendEnergy = hasBackendTotalsColumn(
    data,
    TOTALS_COLUMNS.transferEnergy,
  );
  const useBackendCost = hasBackendTotalsColumn(
    data,
    TOTALS_COLUMNS.transferCost,
  );

  let totalImport = 0;
  let totalExport = 0;
  let exportMax = 0;
  let importMax = 0;
  let totalCost = 0;
  /* The Import and Export rows' money, summed in the loop below rather than in
   * a pass of their own -- the row is already in hand. */
  const importCost = startColumnSum();
  const exportRevenue = startColumnSum();

  for (const entry of data) {
    const transferValue = useBackendEnergy
      ? parseFloat(entry[TOTALS_COLUMNS.transferEnergy]) || 0
      : parseFloat(
          readLooseColumn(entry, [
            "Energy Buy/Sell (kWh)",
            "Utility Grid Import (kWh)-Utility Grid Export (kWh)",
          ]),
        ) || 0;

    if (transferValue > 0) {
      totalExport += transferValue;
      if (transferValue > exportMax) exportMax = transferValue;
    } else if (transferValue < 0) {
      const absVal = -transferValue;
      totalImport += absVal;
      if (absVal > importMax) importMax = absVal;
    }

    const costVal = useBackendCost
      ? parseFloat(entry[TOTALS_COLUMNS.transferCost]) || 0
      : parseFloat(
          readLooseColumn(entry, ["Cost or Return ($)", "Costs Transfer Cost ($)"]),
        ) || 0;
    totalCost += costVal;

    addColumnValue(importCost, entry, TOTALS_COLUMNS.transferImportCost);
    addColumnValue(exportRevenue, entry, TOTALS_COLUMNS.transferExportRevenue);
  }

  return {
    minPower: importMax.toFixed(2),
    maxPower: exportMax.toFixed(2),
    totalImport: totalImport.toFixed(2),
    totalExport: (-totalExport).toFixed(2),
    /* ── The engine's sign, not its negation ──────────────────────────────
     *
     * This was `(-totalCost)`, which flipped `Totals Transfer Cost ($)` and
     * made Transfer the only card of the four to report a cost as a positive
     * number. It was invisible while the card showed no per-flow money: with
     * the Import row now populated, the card read -928,736 on Import above a
     * total of +928,258, which is self-contradictory on its face.
     *
     * Three things break under the negation and are fixed by removing it:
     *   - the identity Production + Consumption + Storage = Transfer on the
     *     energy side (7,965.84 - 471,247.44 - 464,976.40 = -928,258.00);
     *   - the net total, which must come out -2,167,139.05 rather than
     *     -310,623.05;
     *   - agreement with the rest of the app, which already treats a transfer
     *     cost as negative -- cardFlowRows.js's own notes cite it as
     *     "-$925,253", and the card fixtures carry it negative too. */
    value: totalCost.toFixed(2),
    /* Engine sign, and now consistent with `value` above: import is money out
     * and reads negative, export is money in and reads positive. */
    importCost: finishColumnSum(importCost),
    exportRevenue: finishColumnSum(exportRevenue),
  };
};



    useEffect(() => {
    const {
      minPower,
      maxPower,
      totalImport,
      totalExport,
      value,
      importCost,
      exportRevenue,
    } = calculateTransferMetrics(chartData);
    setExportedTransferData({
      minPower,
      maxPower,
      totalImport,
      totalExport,
      value,
      /* Absent rather than zero when the engine did not report it: a seeded
       * "0.00" would be a fabricated number. Absent reaches the card as an
       * em-dash. */
      importCost,
      exportRevenue,
      view: selectedPeriodType,
      selectedPeriod,
    });
  }, [
    chartData,
    selectedPeriodType,
    selectedPeriod,
    setExportedTransferData,
  ]);

  useEffect(() => {
    const {
      minPower,
      maxPower,
      totalImport,
      totalExport,
      value,
      importCost,
      exportRevenue,
    } = calculateTransferMetrics(chartData);

    setOverallExportedTransferData({
      minPower,
      maxPower,
      totalImport,
      totalExport,
      value,
      importCost,
      exportRevenue,
      view: selectedPeriodType,
      selectedPeriod,
    });
  }, [
    chartData,
    selectedPeriodType,
    selectedPeriod,
    setOverallExportedTransferData,
  ]);

  /* One payload, two consumers. The Summary tab reads
   * exportedDemandChargeData and the Charts tab reads the "Overall" copy; when
   * these were two effects they each ran sumDemandChargeForPeriod over the
   * whole daily-demand array, and any change to the payload shape had to be
   * made twice or the two tabs quietly disagreed. Compute once, publish to
   * both setters. */
  const demandChargePayload = useMemo(
    () => ({
      /* The FIGURES, from utils/demandCharge.js. The three device pages build
       * theirs with the same call, which is what stops the Consumption device
       * card and the Summary Consumption card -- the same quantity rendered on
       * two pages -- from ever disagreeing. */
      ...buildDemandChargePayload(
        hasDemandCharge,
        dailyDemand,
        view,
        selDate,
        selMonth,
        selYear,
      ),
      /* The LABELS, which are this surface's own business. */
      view: selectedPeriodType,
      selectedPeriod,
    }),
    [
      dailyDemand,
      hasDemandCharge,
      view,
      selDate,
      selMonth,
      selYear,
      selectedPeriodType,
      selectedPeriod,
    ],
  );

  useEffect(() => {
    setExportedDemandChargeData(demandChargePayload);
    setOverallExportedDemandChargeData(demandChargePayload);
  }, [
    demandChargePayload,
    setExportedDemandChargeData,
    setOverallExportedDemandChargeData,
  ]);

  useEffect(() => {
    /* Not gated on legend visibility. The legend is a view control for this
     * chart; the cards and the power-flow diamond describe the simulation, and
     * since the split put them on their own tab there is nothing left to keep
     * in sync. Hiding a series here used to zero its card over there.
     *
     * Only Transfer and Consumption were ever coupled this way. Production and
     * Storage gate on whether any DEVICE is selected, which is a different
     * question and stays. */
    const { totalArea, totalCost, maxPower, loadCost } =
      calculateConsumptionTotals(chartData);
    setExportedData({
      totalAreaUnderCurve: totalArea,
      totalCost,
      maxPower,
      loadCost,
      view: selectedPeriodType,
      selectedPeriod,
    });
  }, [
    chartData,
    selectedPeriodType,
    selectedPeriod,
    setExportedData,
  ]);

  useEffect(() => {
    const { totalArea, totalCost, maxPower, loadCost } =
      calculateConsumptionTotals(chartData);

    setOverallExportedData({
      totalAreaUnderCurve: totalArea,
      totalCost,
      maxPower,
      loadCost,
      view: selectedPeriodType,
      selectedPeriod,
    });
  }, [
    chartData,
    selectedPeriodType,
    selectedPeriod,
    setOverallExportedData,
  ]);
  /* SummaryPage passes these capacities keyed by device type — { BESS: [...] }
   * and { EV: [...] } — not as bare arrays. Indexing them numerically
   * (`bessMaxCapacity?.[i]`) therefore read `undefined` for every device and
   * collapsed to 0, which made the metrics worker report avgSOCpct as 0 for
   * every device and zeroed the Storage card's avg SoC. Walk the type buckets
   * the same way StorageChart.jsx does. */
  const capLookup = useMemo(() => {
    const caps = {};

    Object.entries(bessMaxCapacity || {}).forEach(([type, values]) => {
      (values || []).forEach((val, i) => {
        const name = deviceNames?.[type]?.[i];
        if (name) caps[name] = +val || 0;
      });
    });

    Object.entries(evMaxCapacity || {}).forEach(([type, values]) => {
      (values || []).forEach((val, i) => {
        const name = deviceNames?.[type]?.[i];
        if (name) caps[name] = +val || 0;
      });
    });

    return caps;
  }, [deviceNames, bessMaxCapacity, evMaxCapacity]);
  
  useEffect(() => {
    if (!chartData.length || !proddeviceNames) return;

    const hasSelectedProductionDevices =
      selectedProductionSummaryDeviceNames.Solar.length > 0 ||
      selectedProductionSummaryDeviceNames.Generator.length > 0;
    if (!hasSelectedProductionDevices) {
      setExportedProductionData([]);
      setExportedProductionSummaryData(zeroProductionSummaryData);
      onLoadingStateChange?.(false);
      return;
    }

    const requestNumber = ++latestProductionRequestRef.current;
    let isCurrent = true;

    // The per-device list (`exportedProductionData`) always comes from the
    // worker so the per-device UI keeps working. Only the *summary* totals
    // can short-circuit to backend columns when every production device is
    // visible (matching what the chart's Production Summary line shows).
    const useBackendTotals =
      areAllProductionDevicesVisible &&
      hasBackendTotalsColumn(chartData, TOTALS_COLUMNS.productionEnergy) &&
      hasBackendTotalsColumn(chartData, TOTALS_COLUMNS.productionCost);

    productionWorkerService
      .run({
        type: "CALCULATE_PRODUCTION_PER_DEVICE",
        data: {
          chartData,
          proddeviceNames: selectedProductionSummaryDeviceNames,
        },

      })
      .then((list) => {
        if (!isCurrent || latestProductionRequestRef.current !== requestNumber) {
          return;
        }

        const stamped = list.map((m) => ({
          ...m,
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        }));
        setExportedProductionData(stamped);
        const summarySeries = buildProductionAggregateSeries(
          chartData,
          selectedProductionSummaryDeviceNames,
        );

        const totalProduction = useBackendTotals
          ? sumBackendColumn(chartData, TOTALS_COLUMNS.productionEnergy)
          : stamped.reduce(
              (sum, item) => sum + (Number(item.totalProduction) || 0),
              0,
            );
        /* The card's own biggest figure, summed the way every ROW figure on
         * the card already is.
         *
         * `Number(item.totalCost) || 0` coerced a device that reported nothing
         * into a contribution of 0 and then fixed the sum to a string, so the
         * total was the one cell on the card that could still fabricate: the
         * rows around it distinguish "no device reported this" from "the
         * devices reported 0" and the total did not. cardNetTotal is built to
         * return null when either half is missing, and that branch was
         * unreachable for the energy half because a null could never arrive.
         * sumDeviceField returns null when NO device reported, which reaches
         * the card as the em-dash. The backend branch keeps sumBackendColumn:
         * it runs only when hasBackendTotalsColumn has already established the
         * column is present. */
        const totalCost = useBackendTotals
          ? sumBackendColumn(chartData, TOTALS_COLUMNS.productionCost).toFixed(2)
          : sumDeviceField(stamped, "totalCost");

        setExportedProductionSummaryData({
          totalProduction: totalProduction.toFixed(2),
          maxPower: calculateSeriesPowerRange(summarySeries).maxPower,
          totalCost,
          /* From the per-device list even when the Totals columns are available,
           * because this card honours device selection and the Totals column
           * does not. With everything visible the two agree exactly. */
          generatedValue: sumDeviceField(stamped, "generatedValue"),
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        });
      
        onLoadingStateChange?.(false);
      })
      .catch(console.error);

    return () => {
      isCurrent = false;
    };
  }, [
    areAllProductionDevicesVisible,
    chartData,
    proddeviceNames,
    selectedProductionSummaryDeviceNames,
    onLoadingStateChange,
    setExportedProductionData,
    setExportedProductionSummaryData,
    zeroProductionSummaryData,
  ]);

  useEffect(() => {
    if (!chartData.length || !proddeviceNames) return;

    const hasProductionDevices =
      allProductionDeviceNames.Solar.length > 0 ||
      allProductionDeviceNames.Generator.length > 0;

    if (!hasProductionDevices) {
      setOverallExportedProductionSummaryData(zeroProductionSummaryData);
      return;
    }

    // Top (always-on) summary card: when the backend exposes the precomputed
    // `Totals Total Production (kWh)` and `Totals Production Cost ($)`
    // columns, use them directly and skip the per-device worker scan.
    const useBackendTotals =
      hasBackendTotalsColumn(chartData, TOTALS_COLUMNS.productionEnergy) &&
      hasBackendTotalsColumn(chartData, TOTALS_COLUMNS.productionCost);

    if (useBackendTotals) {
      const summarySeries = buildProductionAggregateSeries(
        chartData,
        allProductionDeviceNames,
      );
      /* One walk for all three columns. This used to be two calls to
       * sumBackendColumn, i.e. two walks; picking up the generation value here
       * costs a column read per row rather than a third traversal. */
      const totals = sumBackendColumns(chartData, {
        totalProduction: TOTALS_COLUMNS.productionEnergy,
        totalCost: TOTALS_COLUMNS.productionCost,
        generatedValue: TOTALS_COLUMNS.productionGenerationValue,
      });

      setOverallExportedProductionSummaryData({
        totalProduction: totals.totalProduction.total.toFixed(2),
        maxPower: calculateSeriesPowerRange(summarySeries).maxPower,
        totalCost: totals.totalCost.total.toFixed(2),
        generatedValue: finishColumnSum(totals.generatedValue),
        view: selectedPeriodTypeRef.current,
        selectedPeriod: selectedPeriodRef.current,
      });
      return;
    }

    const requestNumber = ++latestOverallProductionRequestRef.current;
    let isCurrent = true;

    productionWorkerService
      .run({
        type: "CALCULATE_PRODUCTION_PER_DEVICE",
        data: {
          chartData,
          proddeviceNames: allProductionDeviceNames,
        },
      })
      .then((list) => {
        if (
          !isCurrent ||
          latestOverallProductionRequestRef.current !== requestNumber
        ) {
          return;
        }

        const stamped = list.map((metric) => ({
          ...metric,
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        }));
        const summarySeries = buildProductionAggregateSeries(
          chartData,
          allProductionDeviceNames,
        );
        const totalProduction = stamped.reduce(
          (sum, item) => sum + (Number(item.totalProduction) || 0),
          0,
        );
        /* Absence-preserving, exactly as the selected-devices effect above --
         * the two publish into the same card shape and must not disagree about
         * what a missing device column means. */
        const totalCost = sumDeviceField(stamped, "totalCost");

        setOverallExportedProductionSummaryData({
          totalProduction: totalProduction.toFixed(2),
          maxPower: calculateSeriesPowerRange(summarySeries).maxPower,
          totalCost,
          generatedValue: sumDeviceField(stamped, "generatedValue"),
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        });
      })
      .catch(console.error);

    return () => {
      isCurrent = false;
    };
  }, [
    allProductionDeviceNames,
    chartData,
    proddeviceNames,
    setOverallExportedProductionSummaryData,
    zeroProductionSummaryData,
  ]);



      useEffect(() => {
        if (!chartData.length || !deviceNames) return; // ← wait until both ready

        const hasSelectedStorageDevices =
          selectedStorageSummaryDeviceNames.BESS.length > 0 ||
          selectedStorageSummaryDeviceNames.EV.length > 0;
        if (!hasSelectedStorageDevices) {
          setExportedStorageData([]);
          setExportedStorageSummaryData(zeroStorageSummaryData);
          onLoadingStateChange?.(false);
          return;
        }

        const requestNumber = ++latestStorageRequestRef.current;
        let isCurrent = true;
    
        storageWorkerService
          .run({
            type: "CALCULATE_PER_DEVICE_LIST",
            data: {
              chartData,
              deviceNames: selectedStorageSummaryDeviceNames,
              maxCapacities: capLookup,
            },
          })
          .then((list) => {
            if (!isCurrent || latestStorageRequestRef.current !== requestNumber) {
              return;
            }

            const stamped = list.map((m) => ({
              ...m,
              view: selectedPeriodTypeRef.current,
              selectedPeriod: selectedPeriodRef.current,
            }));
            setExportedStorageData(stamped);
            const summarySeries = buildStorageAggregateSeries(
              chartData,
              selectedStorageSummaryDeviceNames,
            );
            const totalEnergy = stamped.reduce(
              (sum, item) => sum + (Number(item.totalEnergy) || 0),
              0,
            );
            const chargeTotal = stamped.reduce(
              (sum, item) => sum + (Number(item.chargeTotal) || 0),
              0,
            );
            const dischargeTotal = stamped.reduce(
              (sum, item) => sum + (Number(item.dischargeTotal) || 0),
              0,
            );
            /* The card total, summed the way storageSummaryFlowMoney sums
             * every row figure beside it. See the note on the production
             * total above: `Number(item.value) || 0` made a device that
             * reported nothing contribute a measured-looking 0, which is the
             * one thing this card's rows are careful never to do. */
            const value = sumDeviceField(stamped, "value");
            const avgSOC =
              stamped.length > 0
                ? stamped.reduce(
                    (sum, item) => sum + (Number(item.avgSOC) || 0),
                    0,
                  ) / stamped.length
                : 0;
            const powerRange = calculateSeriesPowerRange(summarySeries);
            setExportedStorageSummaryData({
              totalEnergy: totalEnergy.toFixed(2),
              chargeTotal: chargeTotal.toFixed(2),
              dischargeTotal: dischargeTotal.toFixed(2),
              minPower: powerRange.minPower,
              maxPower: powerRange.maxPower,
              value,
              avgSOC: avgSOC.toFixed(2),
              ...storageSummaryFlowMoney(stamped),
              view: selectedPeriodTypeRef.current,
              selectedPeriod: selectedPeriodRef.current,
            });
        
            onLoadingStateChange?.(false);
          })
          .catch(console.error);

        return () => {
          isCurrent = false;
        };
      }, [
        chartData,
        deviceNames,
        selectedStorageSummaryDeviceNames,
        capLookup,
        onLoadingStateChange,
        setExportedStorageData,
        setExportedStorageSummaryData,
        zeroStorageSummaryData,
      ]);

  useEffect(() => {
    if (!chartData.length || !deviceNames) return;

    const hasStorageDevices =
      allStorageDeviceNames.BESS.length > 0 ||
      allStorageDeviceNames.EV.length > 0;

    if (!hasStorageDevices) {
      setOverallExportedStorageSummaryData(zeroStorageSummaryData);
      return;
    }

    const requestNumber = ++latestOverallStorageRequestRef.current;
    let isCurrent = true;

    storageWorkerService
      .run({
        type: "CALCULATE_PER_DEVICE_LIST",
        data: {
          chartData,
          deviceNames: allStorageDeviceNames,
          maxCapacities: capLookup,
        },
      })
      .then((list) => {
        if (!isCurrent || latestOverallStorageRequestRef.current !== requestNumber) {
          return;
        }

        const stamped = list.map((metric) => ({
          ...metric,
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        }));
        const summarySeries = buildStorageAggregateSeries(
          chartData,
          allStorageDeviceNames,
        );
        const totalEnergy = stamped.reduce(
          (sum, item) => sum + (Number(item.totalEnergy) || 0),
          0,
        );
        const chargeTotal = stamped.reduce(
          (sum, item) => sum + (Number(item.chargeTotal) || 0),
          0,
        );
        const dischargeTotal = stamped.reduce(
          (sum, item) => sum + (Number(item.dischargeTotal) || 0),
          0,
        );
        /* Absence-preserving, as in the selected-devices effect above. */
        const value = sumDeviceField(stamped, "value");
        const avgSOC =
          stamped.length > 0
            ? stamped.reduce(
                (sum, item) => sum + (Number(item.avgSOC) || 0),
                0,
              ) / stamped.length
            : 0;
        const powerRange = calculateSeriesPowerRange(summarySeries);

        setOverallExportedStorageSummaryData({
          totalEnergy: totalEnergy.toFixed(2),
          chargeTotal: chargeTotal.toFixed(2),
          dischargeTotal: dischargeTotal.toFixed(2),
          minPower: powerRange.minPower,
          maxPower: powerRange.maxPower,
          value,
          avgSOC: avgSOC.toFixed(2),
          ...storageSummaryFlowMoney(stamped),
          view: selectedPeriodTypeRef.current,
          selectedPeriod: selectedPeriodRef.current,
        });
      })
      .catch(console.error);

    return () => {
      isCurrent = false;
    };
  }, [
    allStorageDeviceNames,
    capLookup,
    chartData,
    deviceNames,
    setOverallExportedStorageSummaryData,
    zeroStorageSummaryData,
  ]);

  const toggleSeries = useCallback((seriesName, toggleElement = null) => {
    const contentElement =
      typeof document !== "undefined"
        ? document.querySelector(".content")
        : null;
    const contentRect =
      contentElement && typeof contentElement.getBoundingClientRect === "function"
        ? contentElement.getBoundingClientRect()
        : null;
    const toggleRect =
      toggleElement && typeof toggleElement.getBoundingClientRect === "function"
        ? toggleElement.getBoundingClientRect()
        : null;
    const savedContentScrollTop =
      contentElement && Number.isFinite(contentElement.scrollTop)
        ? contentElement.scrollTop
        : null;
    const savedContentScrollLeft =
      contentElement && Number.isFinite(contentElement.scrollLeft)
        ? contentElement.scrollLeft
        : null;
    const savedWindowScrollX =
      typeof window !== "undefined" ? window.scrollX || window.pageXOffset || 0 : 0;
    const savedWindowScrollY =
      typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0;
    const savedToggleViewportTop =
      toggleRect && contentRect ? toggleRect.top - contentRect.top : null;

    const restoreScrollPosition = () => {
      if (contentElement && savedContentScrollTop !== null) {
        contentElement.scrollTop = savedContentScrollTop;
      }
      if (contentElement && savedContentScrollLeft !== null) {
        contentElement.scrollLeft = savedContentScrollLeft;
      }
      if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
        window.scrollTo(savedWindowScrollX, savedWindowScrollY);
      }

      if (
        contentElement &&
        savedToggleViewportTop !== null &&
        typeof document !== "undefined"
      ) {
        const refreshedContentRect = contentElement.getBoundingClientRect();
        const currentToggle = Array.from(
          document.querySelectorAll("button[data-series-name]"),
        ).find(
          (element) => element.getAttribute("data-series-name") === seriesName,
        );

        if (currentToggle) {
          const currentToggleRect = currentToggle.getBoundingClientRect();
          const deltaTop =
            currentToggleRect.top -
            refreshedContentRect.top -
            savedToggleViewportTop;

          if (Number.isFinite(deltaTop) && Math.abs(deltaTop) > 0.5) {
            contentElement.scrollTop += deltaTop;
          }
        }
      }
    };

    setVisibleSeries((prev) => {
      /* Same rule as the render reads, applied to `prev` rather than the
       * rendered map. Raw, a device clicked before the map caught up would read
       * as visible and the click would "hide" an already-hidden device. */
      const isCurrentlyVisible = resolveSeriesVisibility(prev, seriesName);
      return {
        ...prev,
        [seriesName]: !isCurrentlyVisible,
      };
    });

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        restoreScrollPosition();
        window.requestAnimationFrame(restoreScrollPosition);
      });
      window.setTimeout(restoreScrollPosition, 0);
      window.setTimeout(restoreScrollPosition, 100);
      window.setTimeout(restoreScrollPosition, 250);
    } else {
      restoreScrollPosition();
    }
  }, []);
  const getIconClass = (tab) => {
    if (tab === STORAGE_SUMMARY_SERIES_NAME) return "fa fa-battery-half";
    if (tab === PRODUCTION_SUMMARY_SERIES_NAME) return "fa fa-sun";

    /* An EV and a battery are both storage, but only one of them drives, and
     * the legend is where a reader tells them apart at a glance. */
    if (deviceNames?.EV?.includes(tab)) return "fa fa-car-side";
    if (deviceNames?.BESS?.includes(tab)) return "fa fa-battery-half";

    // Check if the tab matches any Solar or Generator devices
    if (
      proddeviceNames?.Solar?.includes(tab) ||
      proddeviceNames?.Generator?.includes(tab)
    )
      return "fa fa-sun";

    // Check for buy/sell or Transfer/Consumption
    if (tab.toLowerCase().includes("buy") || tab.toLowerCase().includes("sell"))
      return "fa fa-clock";

    switch (tab) {
      case "Transfer":
        return "fa fa-exchange";
      case "Consumption":
        return "fa fa-lightbulb";
      case "Pricing":
        return "fa fa-clock";
      default:
        return "fa fa-circle"; // fallback icon
    }
  };
  const getSeriesGroup = (name) => {
    if (name === "Transfer") return "transfer";
    if (name === "Consumption") return "consumption";
    if (name === STORAGE_SUMMARY_SERIES_NAME) return "storage-summary";
    if (name === PRODUCTION_SUMMARY_SERIES_NAME) return "production-summary";
    if (name === "Pricing") return "pricing";
    if (proddeviceNames?.Solar?.includes(name)) return "solar";
    if (proddeviceNames?.Generator?.includes(name)) return "generator";
    if (deviceNames?.BESS?.includes(name) || deviceNames?.EV?.includes(name)) {
      return "storage";
    }
    return "other";
  };
  /* The transfer and pricing chains carry bare legacy column names, so they
   * keep a loose fallback - but it now runs only after every exact key has
   * missed, and it is case-INsensitive where this site used to be case
   * sensitive. That is the fix for the divergence this consolidation exists to
   * remove: on a header whose casing differed, TransferChart resolved a column
   * and plotted a line while this chart silently returned 0 for the same run.
   * See utils/columnLookup.js. */
  const readLooseColumn = useCallback(
    (entry, keys) => readColumn(entry, keys, { fallback: LOOSE }),
    [],
  );
  /* A BESS device's columns are named "<dev> Battery <verb>" by some writers and
   * "<dev> BESS <verb>" by others. This used to hardcode BOTH at once -
   * "Battery" for the split columns and "BESS" for the combined one - so a
   * result using either of the other two combinations plotted a flat zero while
   * the summary cards, which probe the header, reported real energy for the
   * same run. makeStorageFlowReader makes the same probe the cards make, once
   * per row set rather than once per row. See utils/columnLookup.js. */
  const readDeviceFlowSeries = useCallback((rows, name, deviceType) => {
    const read = makeStorageFlowReader(rows, name, deviceType);
    return rows.map((entry) => read(entry) || 0);
  }, []);

  const sumSeriesData = useCallback(
    (rows, seriesList) =>
      rows.map((_, index) =>
        roundChartValue(
          seriesList.reduce(
            (sum, deviceSeries) => sum + (Number(deviceSeries[index]) || 0),
            0,
          ),
        ),
      ),
    [],
  );
  const buildStorageAggregateSeries = useCallback(
    (rows, namesByType) => {
      const seriesList = [
        ...((namesByType?.BESS || []).map((name) =>
          readDeviceFlowSeries(rows, name, "BESS"),
        )),
        ...((namesByType?.EV || []).map((name) =>
          readDeviceFlowSeries(rows, name, "EV"),
        )),
      ];

      return seriesList.length > 0 ? sumSeriesData(rows, seriesList) : [];
    },
    [readDeviceFlowSeries, sumSeriesData],
  );
  const buildProductionAggregateSeries = useCallback(
    (rows, namesByType) => {
      const seriesList = [
        ...((namesByType?.Solar || []).map((name) =>
          rows.map((entry) => Number(entry[`${name} Solar (kWh)`]) || 0),
        )),
        ...((namesByType?.Generator || []).map((name) =>
          rows.map(
            (entry) => Number(entry[`${name} Generator Output (kWh)`]) || 0,
          ),
        )),
      ];

      return seriesList.length > 0 ? sumSeriesData(rows, seriesList) : [];
    },
    [sumSeriesData],
  );
  const calculateSeriesPowerRange = useCallback((seriesData) => {
    if (!Array.isArray(seriesData) || seriesData.length === 0) {
      return {
        minPower: "0.00",
        maxPower: "0.00",
      };
    }

    let minPower = Infinity;
    let maxPower = -Infinity;

    for (const value of seriesData) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) continue;
      if (numericValue < minPower) minPower = numericValue;
      if (numericValue > maxPower) maxPower = numericValue;
    }

    return {
      minPower: (minPower === Infinity ? 0 : minPower).toFixed(2),
      maxPower: (maxPower === -Infinity ? 0 : maxPower).toFixed(2),
    };
  }, []);

  const getSeriesColor = useCallback(
    (name) => {
      switch (name) {
        /* Imported, not typed here. deviceColors.js measures every device
         * colour against these four, and it can only do that honestly if the
         * chart paints the same strings the sweep reads - see the note on the
         * summary-line exports there. Consumption in particular used to be
         * `rgba(255, 30, 30, 1)`: the same colour, in a notation the palette
         * test's hex parser cannot read. */
        case "Transfer":
          return TRANSFER_LINE_COLOR;
        case "Consumption":
          return CONSUMPTION_LINE_COLOR;
        case STORAGE_SUMMARY_SERIES_NAME:
          return STORAGE_SUMMARY_LINE_COLOR;
        case PRODUCTION_SUMMARY_SERIES_NAME:
          return PRODUCTION_SUMMARY_LINE_COLOR;
        default: {
          /* Per device, not per category. Four EVs used to be four strokes -
           * eight counting their dashed driving twins - in one identical green,
           * with a third to a half of each one drawn on top of another at the
           * zero line, which is what "multiple line graphs ... that overlap"
           * was reporting. See utils/deviceColors.js. */
          const fromRoster = (roster, base, category) => {
            const index = roster?.indexOf(name) ?? -1;
            return index === -1 ? null : getDeviceColor(base, index, category);
          };
          return (
            fromRoster(deviceNames?.BESS, BESS_BASE_COLOR) ??
            fromRoster(deviceNames?.EV, null, "EV") ??
            /* The measured bases, not the inherited literals. This chart is
             * the reason they were measured at all - it is the only one that
             * draws a solar array, a generator, every EV, the battery and all
             * four summary lines onto ONE axis - and it was the last one still
             * painting the superseded values: #8E24AA sat ΔE 2.45 from the
             * deep-violet EV #9628A1 and #FF9D41 sat ΔE 5.99 from the gold
             * Production Summary line, so a site with a generator and six EVs
             * drew two of its strokes in what is effectively one violet. */
            fromRoster(proddeviceNames?.Solar, SOLAR_BASE_COLOR) ??
            fromRoster(proddeviceNames?.Generator, GENERATOR_BASE_COLOR) ??
            "#888"
          );
        }
      }
    },
    [deviceNames, proddeviceNames],
  );

  const getBackgroundDeviceStyle = useCallback(
    (name) => {
      const baseColor = getSeriesColor(name);
      const isAnySeriesHighlighted = Boolean(highlightedSeriesName);
      const mutedColor = withAlpha(
        baseColor,
        isAnySeriesHighlighted
          ? DIMMED_DEVICE_LINE_ALPHA
          : BACKGROUND_DEVICE_LINE_ALPHA,
      );
      const isHighlighted = name === highlightedSeriesName;

      /* Under replaceMerge with ids (see the update at the bottom of this
       * file), an omitted key is a RETAINED key: echarts merges the new option
       * onto the model it already holds for that id, so a property the new
       * option leaves out keeps its previous value. Dropping shadowBlur when a
       * line stops being highlighted therefore burned the 8px glow in forever
       * and the highlight stopped meaning anything. Every style property this
       * file varies has to be stated in BOTH branches, never spread in on one. */
      return {
        lineStyle: {
          width: isHighlighted ? HIGHLIGHTED_SUMMARY_LINE_WIDTH : CHART_LINE_WIDTH,
          color: isHighlighted ? baseColor : mutedColor,
          shadowBlur: isHighlighted ? 8 : 0,
          shadowColor: isHighlighted
            ? withAlpha(baseColor, 0.35)
            : "transparent",
        },
        itemStyle: { color: isHighlighted ? baseColor : mutedColor },
        z: isHighlighted ? 12 : 2,
      };
    },
    [getSeriesColor, highlightedSeriesName],
  );
  const isSeriesHighlighted = useCallback(
    (name) => {
      if (!highlightedSeriesName) return false;
      if (name === highlightedSeriesName) return true;

      return (
        highlightedSeriesName === "Pricing" &&
        pricingSeriesNames.includes(name)
      );
    },
    [highlightedSeriesName, pricingSeriesNames],
  );
  const getSummaryLineStyle = useCallback(
    (name, { color, dashed = false, baseZ = 3 } = {}) => {
      const resolvedColor = color || getSeriesColor(name);
      const isHighlighted = isSeriesHighlighted(name);
      const isAnySeriesHighlighted = Boolean(highlightedSeriesName);
      const nonHighlightedColor = withAlpha(
        resolvedColor,
        isAnySeriesHighlighted ? DIMMED_SUMMARY_LINE_ALPHA : 1,
      );

      /* Under replaceMerge with ids (see the update at the bottom of this
       * file), an omitted key is a RETAINED key: echarts merges the new option
       * onto the model it already holds for that id, so a property the new
       * option leaves out keeps its previous value. Dropping shadowBlur when a
       * line stops being highlighted therefore burned the 8px glow in forever
       * and the highlight stopped meaning anything. Every style property this
       * file varies has to be stated in BOTH branches, never spread in on one. */
      return {
        lineStyle: {
          width: isHighlighted ? HIGHLIGHTED_SUMMARY_LINE_WIDTH : CHART_LINE_WIDTH,
          color: isHighlighted ? resolvedColor : nonHighlightedColor,
          type: dashed ? "dashed" : "solid",
          shadowBlur: isHighlighted ? 8 : 0,
          shadowColor: isHighlighted
            ? withAlpha(resolvedColor, 0.35)
            : "transparent",
        },
        itemStyle: { color: isHighlighted ? resolvedColor : nonHighlightedColor },
        z: isHighlighted ? 12 : baseZ,
      };
    },
    [getSeriesColor, highlightedSeriesName, isSeriesHighlighted],
  );
  const handleLegendAreaHover = useCallback((event) => {
    const legendTarget = event.target?.closest?.("[data-series-name]");
    const seriesName = legendTarget?.getAttribute?.("data-series-name") || "";
    if (seriesName) {
      setHoveredLegendSeriesName(seriesName);
    }
  }, []);


  /* The Date column reaches here as whatever string the result file carried -
   * "01/01/2040 00:00" from the sim, ISO from other writers - so every period
   * key below comes from a parse rather than a fixed offset into the stamp.
   * "01/01/2040".slice(0, 4) is "01/0", which matches Jan 1-9 of any year: a
   * yearly view built that way both truncates and cross-contaminates years.
   * One parse per row, positionally aligned with dailyData. */
  const periodKeys = useMemo(
    () => dailyData.map((row) => parseSimulationDate(row?.Date)),
    [dailyData],
  );

  /* First and last stamp the result actually covers. The native pickers below
   * reject a min/max they cannot read as ISO, which these keys always are. */
  const [firstKey, lastKey] = useMemo(() => {
    const readable = periodKeys.filter(Boolean);
    return [readable[0] ?? null, readable[readable.length - 1] ?? null];
  }, [periodKeys]);

  const availableYears = useMemo(
    () => Array.from(new Set(periodKeys.filter(Boolean).map((key) => key.year))),
    [periodKeys],
  );

  // Initialize selectors once
  // Reset selectors whenever view OR dailyData changes
  useEffect(() => {
    if (!firstKey) return;
    if (view === "daily") {
      setSelDate(firstKey.day);
    } else if (view === "monthly") {
      setSelMonth(firstKey.month);
    } else if (view === "yearly") {
      setSelYear(firstKey.year);
    }
  }, [firstKey, view]);

  /* Falls back to the first period the result covers rather than slicing on an
   * empty selector. The selectors are filled by an effect, so for one render
   * after a view switch the new view's selector is still "". The offset-slicing
   * version this replaced papered over that case only for the daily view, and
   * only by accident: `Date.startsWith("")` is true of every row, so switching
   * to Daily briefly plotted the WHOLE dataset under a one-day label, while
   * switching to Monthly or Yearly plotted nothing. Slicing on the first period
   * instead shows the right rows immediately in all three, and keeps the chart
   * mounted - an empty chartData unmounts ReactECharts, which disposes the
   * instance and takes the user's zoom range and legend selection with it. */
  const activeDay = selDate || firstKey?.day || "";
  const activeMonth = selMonth || firstKey?.month || "";
  const activeYear = selYear || firstKey?.year || "";

  // Slice rows whenever view changes
  useEffect(() => {
    if (!dailyData.length) return;
    let rows = [];
    if (view === "daily")
      rows = dailyData.filter((_, i) => periodKeys[i]?.day === activeDay);
    if (view === "monthly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.month === activeMonth);
    if (view === "yearly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.year === activeYear);
    if (view === "all time view") rows = dailyData;
    setChartData(rows);
  }, [view, activeDay, activeMonth, activeYear, dailyData, periodKeys]);

  // Notify parent about loading
  useEffect(() => {
    onLoadingStateChange?.(isLoading || !chartData.length);
  }, [isLoading, chartData.length, onLoadingStateChange]);

  /* ── Reading the period, and drawing it, are two different jobs ─────────
   *
   * Everything on this chart is READ out of the period's rows and then STYLED
   * according to what is visible and what is hovered. Those run on different
   * clocks: the reading changes only when the period does, the styling changes
   * on every legend click and every hover.
   *
   * They used to share one memo keyed on both, so toggling a single device
   * re-read every column of every device and rebuilt every summary line from
   * the raw rows again. Measured on the husky-2040 production year - 8784 rows,
   * six EVs and a battery - that was ~165ms of pure re-reading on every click,
   * on top of the ECharts update the click actually needs. Splitting them means
   * a click only reassembles series objects around arrays that already exist.
   *
   * What is NOT split out: the aggregate sums below. They depend on which
   * devices are selected, so they belong on the styling side - and they are
   * summed from `storageValues`, which this memo builds for EVERY device
   * whether or not it is drawn. See the note on the Storage Summary line. */
  const periodData = useMemo(() => {
    const rows = view === "all time view" ? allTimeData : chartData;

    const storageOrder = [
      ...(Array.isArray(deviceNames?.BESS) ? deviceNames.BESS : []).map(
        (name) => ({ name, kind: "BESS" }),
      ),
      ...(Array.isArray(deviceNames?.EV) ? deviceNames.EV : []).map((name) => ({
        name,
        kind: "EV",
      })),
    ];

    /* Complete, for every device, always. The Storage Summary is summed from
     * these, so they cannot become conditional on what is on screen without
     * the aggregate silently becoming a subtotal. */
    const storageValues = {};
    /* The two raw columns each EV's strokes are built from, kept so the strokes
     * can be built later without going back to the rows. */
    const evStrokeInputs = {};

    storageOrder.forEach(({ name, kind }) => {
      const plugFlow = readDeviceFlowSeries(rows, name, kind);
      if (kind === "BESS") {
        storageValues[name] = plugFlow;
        return;
      }
      const driving = rows.map((entry) => getEVDriving(entry, name));
      evStrokeInputs[name] = { plugFlow, driving };
      /* The same helper buildTwinStrokeData uses, so the aggregate and the
       * strokes cannot disagree about what a device's value is. */
      storageValues[name] = twinStrokeValues(plugFlow, driving);
    });

    const productionOrder = [
      ...(Array.isArray(proddeviceNames?.Solar) ? proddeviceNames.Solar : []).map(
        (name) => ({ name, kind: "Solar" }),
      ),
      ...(Array.isArray(proddeviceNames?.Generator)
        ? proddeviceNames.Generator
        : []
      ).map((name) => ({ name, kind: "Generator" })),
    ];
    const productionValues = {};
    productionOrder.forEach(({ name, kind }) => {
      const column =
        kind === "Solar"
          ? `${name} Solar (kWh)`
          : `${name} Generator Output (kWh)`;
      productionValues[name] = rows.map((entry) => Number(entry[column]) || 0);
    });

    return {
      rows,
      consumption: rows.map((entry) => {
        const value =
          parseFloat(
            readColumn(entry, ["Load (kWh)", "Building Load Load (kWh)"]),
          ) || 0;
        return -Math.abs(value);
      }),
      transfer: rows.map((entry) =>
        -parseFloat(
          readLooseColumn(entry, [
            "Totals Total Transfer (kWh)",
            "Energy Buy/Sell (kWh)",
            "Utility Grid Import (kWh)-Utility Grid Export (kWh)",
          ]),
        ),
      ),
      storageOrder,
      storageValues,
      evStrokeInputs,
      productionOrder,
      productionValues,
    };
  }, [
    view,
    allTimeData,
    chartData,
    deviceNames,
    proddeviceNames,
    readDeviceFlowSeries,
    readLooseColumn,
  ]);

  /* ── What is switched off at load, built off the critical path ─────────
   *
   * Two things on this chart are read but not shown when the page opens: an
   * EV's two strokes and two connector subpaths (see utils/twinStrokeSeries.js)
   * for every device, since devices start switched off, and the tariff pair,
   * since Pricing does too. Neither is large - ~15ms for six EVs and ~50ms for
   * the tariff pair on a production year - but neither is on the critical path
   * for anything, so both come off it and are warmed in idle time instead.
   *
   * Held in a ref rather than in state ON PURPOSE. Filling this cache changes
   * no rendered output, so the background pass below causes no re-render and,
   * crucially, no new moment when the SERIES SET changes. The series set still
   * changes only when the user toggles something, exactly as before - which is
   * what the tooltip guard in the setOption effect below is about, and what
   * TooltipSeriesChange.test.jsx pins.
   *
   * A miss still builds synchronously, so a click can never WAIT on the
   * background pass having got there first; it is one entry's worth of work.
   * The pass just means it has normally already happened. */
  const deferredCacheRef = useRef({ source: null, entries: new Map() });
  const getDeferred = useCallback(
    (key, build) => {
      const cache = deferredCacheRef.current;
      if (cache.source !== periodData) {
        cache.source = periodData;
        cache.entries = new Map();
      }
      if (cache.entries.has(key)) return cache.entries.get(key);
      const built = build();
      cache.entries.set(key, built);
      return built;
    },
    [periodData],
  );

  const getDeviceStrokes = useCallback(
    (deviceName) => {
      const input = periodData.evStrokeInputs[deviceName];
      if (!input) return null;
      return getDeferred(`strokes:${deviceName}`, () =>
        buildTwinStrokeData(input.plugFlow, input.driving),
      );
    },
    [periodData, getDeferred],
  );

  const getPricingData = useCallback(
    () =>
      getDeferred("pricing", () => ({
        sell: periodData.rows.map(
          (entry) =>
            -readLooseColumn(entry, ["Sell ($/kWh)", "Utility Sell ($/kWh)"]),
        ),
        buy: periodData.rows.map((entry) =>
          readLooseColumn(entry, ["Buy ($/kWh)", "Utility Buy ($/kWh)"]),
        ),
      })),
    [periodData, getDeferred, readLooseColumn],
  );

  useEffect(() => {
    const pending = [
      ...Object.keys(periodData.evStrokeInputs).map(
        (name) => () => getDeviceStrokes(name),
      ),
      () => getPricingData(),
    ];

    let cancelled = false;
    let handle = null;
    /* One entry per slice, so a microgrid with many EVs cannot turn this into
     * a single long task of its own. requestIdleCallback where it exists -
     * Safari still does not have it, and neither does jsdom. */
    const schedule =
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
        ? (callback) => window.requestIdleCallback(callback)
        : (callback) => setTimeout(callback, 0);
    const unschedule =
      typeof window !== "undefined" &&
      typeof window.cancelIdleCallback === "function"
        ? (id) => window.cancelIdleCallback(id)
        : (id) => clearTimeout(id);

    let index = 0;
    const step = () => {
      if (cancelled) return;
      pending[index]();
      index += 1;
      if (index < pending.length) handle = schedule(step);
    };
    handle = schedule(step);

    return () => {
      cancelled = true;
      if (handle !== null) unschedule(handle);
    };
  }, [periodData, getDeviceStrokes, getPricingData]);

  // Prepare series
  const series = useMemo(() => {
    const { rows } = periodData;

    const out = [];
    const allStorageSeriesData = [];
    const allProductionSeriesData = [];

    if (isSeriesVisible("Consumption")) {
      out.push({
        id: "Consumption",
        name: "Consumption",
        type: "line",
        /* Building load is a CONTINUOUS quantity: it varies through the hour
         * and the figure in the row is an average of it, not a setpoint held
         * flat. A step would assert the draw jumped on the hour and sat still
         * between - see HOURLY_BUCKET_STEP in utils/chartOptions.js. Restored
         * to exactly the rendering it had before the step pass. */
        smooth: true,
        data: periodData.consumption,
        ...getSummaryLineStyle("Consumption", { baseZ: 4 }),
        symbol: "none",
      });
    }

    if (isSeriesVisible("Transfer")) {
      out.push({
        id: "Transfer",
        name: "Transfer",
        type: "line",
        /* The residual of everything else, so it is mixed by construction: the
         * dispatched terms step, the load and solar terms do not. It cannot be
         * flat across an hour in which the load it balances is not, so it goes
         * with the continuous half and keeps the rendering it had.
         *
         * The strongest argument the other way, recorded because it is real and
         * was measured rather than waved at: on husky-2040 the hour-to-hour
         * movement of this line is dominated by dispatch, not by load - a
         * standard deviation of 658 kWh against load's 74, exceeding the load
         * change in 53% of hours, with a largest single-hour swing of 2621 kWh
         * against load's 903. So most of the visible shape here IS piecewise
         * constant, and smoothing rounds those dispatch jumps. It still goes
         * unstepped: the rule is about what the underlying quantity does, not
         * about which term happens to dominate the picture, and TransferChart
         * renders the same quantity the same way. Easy to reverse if the
         * rounded jumps read badly. */
        smooth: true,
        data: periodData.transfer,
        ...getSummaryLineStyle("Transfer", { baseZ: 4 }),
        symbol: "none",
      });
    }

    /* An EV is in exactly one state per interval: charging, discharging through
     * the plug, or away and driving, so one line per device can carry all three
     * and only the stroke has to change. Each device becomes two series under
     * one name and one colour — solid for plug flow, dashed for driving on the
     * positive axis — joined at every transition by utils/twinStrokeSeries.js,
     * exactly as on the Storage page. The aggregate below is summed from the
     * per-device `values`, never from the rendered strokes, because the two
     * repeat a sample between them at each join and summing those would
     * double-count it. Those `values` are read for every device up in
     * periodData, so the aggregate is whole whether or not any device is drawn;
     * only the strokes are conditional.
     *
     * Under HOURLY_BUCKET_STEP the join gets sharper rather than weaker. At a
     * hand-over both strokes carry a 0, so the solid one runs flat at its last
     * plug reading across that hour and drops to 0 exactly on the boundary,
     * while the dashed one runs flat at 0 across the same hour and rises from 0
     * on the same boundary. The two verticals are collinear and meet at the
     * axis: one unbroken vertical from plug flow through zero to driving, half
     * of it solid and half dashed. The smoothed version fitted a curve through
     * those same zeros instead, which is what rounded every hand-over into a
     * swoop and made the halves look as though they overlapped. */
    periodData.storageOrder.forEach(({ name, kind }) => {
      const values = periodData.storageValues[name];
      allStorageSeriesData.push(values);
      if (!isSeriesVisible(name)) return;

      if (kind === "BESS") {
        out.push({
          id: name,
          name,
          type: "line",
          step: HOURLY_BUCKET_STEP,
          data: values,
          ...getBackgroundDeviceStyle(name),
          symbol: "none",
        });
        return;
      }

      /* Normally already built by the background pass above; a miss builds it
       * here and now rather than making the click wait for one. */
      const strokes = getDeviceStrokes(name);
      if (!strokes) return;
      const { solid, dashed, connectors, hasDriving } = strokes;
      {
        const deviceStyle = getBackgroundDeviceStyle(name);
        out.push({
          id: name,
          name,
          type: "line",
          step: HOURLY_BUCKET_STEP,
          data: solid,
          ...deviceStyle,
          symbol: "none",
        });
        /* A device that never drove in this period — a legacy result with no
         * driving column, or simply a vehicle that stayed put — gets no dashed
         * twin at all, so its line is 100% solid like a battery. */
        if (hasDriving) {
          out.push({
            id: drivingSeriesIdFor(name),
            name,
            type: "line",
            step: HOURLY_BUCKET_STEP,
            data: dashed,
            ...deviceStyle,
            lineStyle: { ...deviceStyle.lineStyle, type: "dashed" },
            symbol: "none",
          });
          /* The hand-over verticals. Deliberately NOT stepped: they are
           * explicit [column, value] pairs at a single column, so they draw a
           * vertical of no width and own no interval - which is the only way to
           * close the gap at a hand-over without one stroke painting an hour
           * that belongs to the other. Same name as the device so the legend
           * toggles all of it together, same colour, matching dash. */
          out.push(
            {
              id: connectorSeriesIdFor(name),
              name,
              type: "line",
              data: connectors.solid,
              ...deviceStyle,
              symbol: "none",
            },
            {
              id: drivingConnectorSeriesIdFor(name),
              name,
              type: "line",
              data: connectors.dashed,
              ...deviceStyle,
              lineStyle: { ...deviceStyle.lineStyle, type: "dashed" },
              symbol: "none",
            },
          );
        }
      }
    });

    if (
      isSeriesVisible(STORAGE_SUMMARY_SERIES_NAME) &&
      allStorageSeriesData.length > 0
    ) {
      out.push({
        id: STORAGE_SUMMARY_SERIES_NAME,
        name: STORAGE_SUMMARY_SERIES_NAME,
        type: "line",
        step: HOURLY_BUCKET_STEP,
        /* Every device, never only the visible ones. The aggregate is the
         * whole fleet by contract - see the note above and StorageChart.jsx -
         * so switching one device on must not shrink the total. */
        data: sumSeriesData(rows, allStorageSeriesData),
        ...getSummaryLineStyle(STORAGE_SUMMARY_SERIES_NAME, {
          baseZ: 5,
        }),
        symbol: "none",
      });
    }

    // Production devices
    periodData.productionOrder.forEach(({ name, kind }) => {
      const data = periodData.productionValues[name];
      allProductionSeriesData.push(data);
      if (!isSeriesVisible(name)) return;
      out.push({
        id: name,
        name,
        type: "line",
        /* Irradiance varies continuously through the hour, so the figure is an
         * average and not a setpoint: the solar line carries NEITHER `step` NOR
         * `smooth`, and so draws straight between points rather than curved.
         * ProductionChart's solar line is the same.
         *
         * A generator is the other case - commanded to a setpoint that
         * genuinely holds for the interval - so it steps. No result in the
         * corpus carries a generator today: 186 timeseries files, none with a
         * "Generator Output" column, so that is the rule applied ahead of the
         * data rather than something visible on any run we hold. */
        ...(kind === "Generator" ? { step: HOURLY_BUCKET_STEP } : {}),
        data,
        ...getBackgroundDeviceStyle(name),
        symbol: "none",
      });
    });

    if (
      isSeriesVisible(PRODUCTION_SUMMARY_SERIES_NAME) &&
      allProductionSeriesData.length > 0
    ) {
      out.push({
        id: PRODUCTION_SUMMARY_SERIES_NAME,
        name: PRODUCTION_SUMMARY_SERIES_NAME,
        type: "line",
        /* Solar plus generator, so in principle one of each kind - and the only
         * aggregate here where the answer is not read straight off its parts.
         * It follows solar, for a reason that is checkable rather than a lean:
         * across all 186 timeseries results in Grene.DT.sim Prod/projects there
         * is no generator at all - every production column in the corpus is
         * "Solar (kWh)" - so on every run this chart has ever drawn, this line
         * IS the solar line and inherits its continuity whole. Revisit if a
         * generator ever appears and carries a material share; the sum would
         * then be genuinely mixed and neither rendering would fit it. Restored
         * to exactly the rendering it had before the step pass. */
        smooth: true,
        /* Every device, never only the visible ones - same contract as the
         * Storage Summary above. */
        data: sumSeriesData(rows, allProductionSeriesData),
        ...getSummaryLineStyle(PRODUCTION_SUMMARY_SERIES_NAME, {
          baseZ: 5,
        }),
        symbol: "none",
      });
    }

    /* Pricing (two series, controlled by one toggle).
     *
     * A tariff is piecewise constant BY DEFINITION - a rate period holds one
     * price flat and changes on the hour - so these step, and they are the one
     * place on this chart where the step is not an approximation of anything.
     * Smoothing rounded off the rate boundaries, which are the most
     * decision-relevant feature of a price curve and the thing an arbitrage
     * result is read against.
     *
     * That the rate stamped t is the rate in force FROM t, matching the energy
     * columns, was measured rather than assumed - see the tariff note on
     * HOURLY_BUCKET_STEP in utils/chartOptions.js. PricingChart renders the
     * same two quantities the same way. */
    if (isSeriesVisible("Pricing")) {
      /* Normally already read by the background pass above, since Pricing is
       * off when the page opens; a miss reads it here and now. */
      const pricing = getPricingData();
      out.push(
        {
          id: pricingSeriesNames[0],
          name: pricingSeriesNames[0], // Sell ($/kWh)
          type: "line",
          step: HOURLY_BUCKET_STEP,
          yAxisIndex: 1,
          data: pricing.sell,
          ...getSummaryLineStyle(pricingSeriesNames[0], {
            color: "#2A26A7",
            baseZ: 4,
          }),
          symbol: "none",
        },
        {
          id: pricingSeriesNames[1],
          name: pricingSeriesNames[1], // Buy ($/kWh)
          type: "line",
          step: HOURLY_BUCKET_STEP,
          yAxisIndex: 1,
          data: pricing.buy,
          ...getSummaryLineStyle(pricingSeriesNames[1], {
            color: "#A7262A",
            baseZ: 4,
          }),
          symbol: "none",
        },
      );
    }

    return out;
  }, [
    periodData,
    getDeviceStrokes,
    getPricingData,
    isSeriesVisible,
    pricingSeriesNames,
    getBackgroundDeviceStyle,
    getSummaryLineStyle,
    sumSeriesData,
  ]);

  const axisExtents = useMemo(() => {
    const powerSeries = series.filter((s) => (s.yAxisIndex ?? 0) === 0);
    const pricingSeries = series.filter((s) => (s.yAxisIndex ?? 0) === 1);

    const getMinMax = (arr) => {
      let min = Infinity;
      let max = -Infinity;
      for (const s of arr) {
        const data = Array.isArray(s.data) ? s.data : [];
        for (const v0 of data) {
          // A twin stroke carries tagged objects and nulls where its partner
          // is drawn; the gaps must not pull the range towards 0.
          const v = twinStrokeValue(v0);
          if (v === null) continue;
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
      if (min === Infinity || max === -Infinity) return null;
      return { min, max };
    };

    const p = getMinMax(powerSeries);
    const pr = getMinMax(pricingSeries);

    const overall = (() => {
      const mins = [];
      const maxs = [];
      if (p) {
        mins.push(p.min);
        maxs.push(p.max);
      }
      if (pr) {
        mins.push(pr.min);
        maxs.push(pr.max);
      }
      if (!mins.length) return null;
      return { min: Math.min(...mins), max: Math.max(...maxs) };
    })();

    const hasPos = overall ? overall.max > 0 : false;
    const hasNeg = overall ? overall.min < 0 : false;
    const centerZero = hasPos && hasNeg;

    const scaleFor = (mm) => {
      if (!mm) return {};
      if (centerZero) {
        const a = Math.max(Math.abs(mm.min), Math.abs(mm.max), 1e-6);
        return {
          min: roundChartValue(-a),
          max: roundChartValue(a),
        };
      }
      // One-sided: anchor at 0 on the empty side to avoid wasted space.
      if (mm.max <= 0) {
        return { min: roundChartValue(mm.min), max: 0 };
      }
      if (mm.min >= 0) {
        return { min: 0, max: roundChartValue(mm.max) };
      }
      // Fallback (shouldn't happen if centerZero is correct), but keep visible.
      return {
        min: roundChartValue(mm.min),
        max: roundChartValue(mm.max),
      };
    };

    return {
      power: scaleFor(p),
      pricing: scaleFor(pr),
    };
  }, [series]);
  const hasVisiblePowerAxisExtents = useMemo(
    () =>
      Number.isFinite(axisExtents?.power?.min) &&
      Number.isFinite(axisExtents?.power?.max),
    [axisExtents],
  );

  useEffect(() => {
    if (!hasVisiblePowerAxisExtents) return;

    setLastVisiblePowerAxisExtents((previousExtents) => {
      if (
        previousExtents?.min === axisExtents.power.min &&
        previousExtents?.max === axisExtents.power.max
      ) {
        return previousExtents;
      }

      return axisExtents.power;
    });
  }, [axisExtents, hasVisiblePowerAxisExtents]);

  const effectivePowerAxisExtents = hasVisiblePowerAxisExtents
    ? axisExtents.power
    : lastVisiblePowerAxisExtents;
  const isPricingOnlyView =
    isSeriesVisible("Pricing") && !hasVisiblePowerAxisExtents;

  /* Extremes per legend entry, not per series: the two strokes of one EV share
   * a name, so they have to be folded together rather than the dashed one
   * overwriting the solid one's numbers. */
  const seriesExtremesByName = useMemo(() => {
    const extremes = {};
    series.forEach((entry) => {
      const current = extremes[entry.name] || { max: -Infinity, min: Infinity };
      (Array.isArray(entry.data) ? entry.data : []).forEach((datum) => {
        const value = twinStrokeValue(datum);
        if (value === null) return;
        if (value > current.max) current.max = value;
        if (value < current.min) current.min = value;
      });
      extremes[entry.name] = current;
    });
    return extremes;
  }, [series]);
  const seriesMaxByName = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(seriesExtremesByName).map(([name, { max }]) => [
          name,
          formatChartValue(max === -Infinity ? 0 : max),
        ]),
      ),
    [seriesExtremesByName],
  );
  const seriesMinByName = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(seriesExtremesByName).map(([name, { min }]) => [
          name,
          formatChartValue(min === Infinity ? 0 : min),
        ]),
      ),
    [seriesExtremesByName],
  );

  /* One label per row, off the SAME rows the series are built from.
   *
   * This used to map `chartData` while every series array was built to
   * `periodData.rows.length`, and in the all-time view those are two different
   * expressions: periodData reads `view === "all time view" ? allTimeData :
   * chartData`. They agree today only by accident - SimulationDataContext calls
   * setDailyData and setAllTimeData with the same array, and the slice effect
   * above assigns chartData = dailyData for that view - so allTimeData is
   * currently a second name for dailyData. The moment it becomes what its name
   * suggests, a coarser aggregate, every series would silently gain or lose
   * points against the category axis, and ECharts renders that without
   * complaint by truncating or leaving a tail unlabelled.
   *
   * Reading one source makes the two lengths equal by construction rather than
   * by coincidence, so there is nothing left to assert.
   *
   * Still formatted once per period rather than once per option rebuild: the
   * option is rebuilt on every legend click, every hover and every zoom step,
   * and re-formatting a production year's stamps each time was ~11ms of work
   * that could not produce a different answer. `periodRows` is the identity the
   * memo keys on, not `periodData`, so a roster change that rebuilds periodData
   * over the same rows does not re-format them. */
  const periodRows = periodData.rows;
  const xAxisLabels = useMemo(
    () => periodRows.map((entry) => formatDate(entry.Date)),
    [periodRows],
  );

  const echartOptions = useMemo(
    () => ({
      animation: true,
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const tooltipParams = Array.isArray(params) ? params : [params];
          const axisLabel =
            tooltipParams[0]?.axisValueLabel ?? tooltipParams[0]?.axisValue ?? "";

          /* A driving sample sits on the positive axis alongside plug
           * discharge, so the label is what tells them apart. The other stroke
           * of the same device is a gap here, and a join is a rendering device
           * rather than a reading — neither belongs in the tooltip. */
          const seriesLines = [];
          tooltipParams.forEach((item) => {
            const rawValue = Array.isArray(item.value)
              ? item.value[1]
              : item.value;
            const point = readTwinStrokePoint(item.data, rawValue);
            if (!point) return;
            const label = isDrivingPoint(point)
              ? `${item.seriesName} (Driving)`
              : item.seriesName;
            /* Formatted through utils/formatNumber, the same helper the
             * summary cards use, so a value read off the tooltip and the same
             * value read off the card beside it now agree. Previously
             * "1234.50" here against the card's "1,234.50". The y-axis label
             * formatter below deliberately keeps the separator-free form: axis
             * ticks are a different context, and the other five charts render
             * their ticks with ECharts' plain default. */
            seriesLines.push(
              `${item.marker}${label}&nbsp;&nbsp;&nbsp;&nbsp;${formatWithCommas(point.value)}`,
            );
          });

          return [axisLabel, ...seriesLines].join("<br/>");
        },
      },
      legend: {
        show: false,
        selectedMode: "multiple",
      },
      dataZoom: buildDataZoom({
        chrome: false,
        insideGestures: false,
        start: zoomRange.start,
        end: zoomRange.end,
      }),
      grid: {
        left: 30,
        right: 40,
        top: 40,
        bottom: 80,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisLabels,
        name: xAxisTitle,
        nameLocation: "middle",
        nameGap: 40,
        axisLine: {
          lineStyle: {
            color: "#999",
            width: 1,
          },
        },
      },
      yAxis: [
        {
          type: "value",
          name: isPricingOnlyView ? "" : "Energy (kWh)",
          nameLocation: "middle",
          nameGap: 55,
          min: effectivePowerAxisExtents?.min,
          max: effectivePowerAxisExtents?.max,
        axisLine: {
          show: !isPricingOnlyView,
          lineStyle: {
            color: "#999",
            width: 1,
          },
        },
        axisTick: {
          show: !isPricingOnlyView,
        },
        axisLabel: {
          show: !isPricingOnlyView,
          formatter: (value) => formatChartValue(value),
        },
        splitLine: {
          show: true,
          lineStyle: {
              type: "dashed",
              color: "#ddd",
            },
          },
        },
        {
          type: "value",
          name: "Pricing ($/kWh)",
          position: "right",
          nameLocation: "middle",
          nameGap: 60,
          min: axisExtents?.pricing?.min,
          max: axisExtents?.pricing?.max,
        axisLine: {
          lineStyle: {
            color: "#999",
            width: 1,
          },
        },
        axisLabel: {
          formatter: (value) => formatChartValue(value),
        },
        splitLine: {
          show: false,
        },
        },
      ],
      series,
    }),
    [
      xAxisLabels,
      series,
      xAxisTitle,
      axisExtents,
      effectivePowerAxisExtents,
      isPricingOnlyView,
      zoomRange,
    ],
  );

  const chartEvents = useMemo(
    () => ({
      datazoom: handleDataZoom,
    }),
    [handleDataZoom],
  );

  // Drive ECharts updates imperatively with replaceMerge so series toggled off
  // are removed cleanly while keeping the chart instance (and its tooltip DOM
  // and animation state) alive across zoom/hover/data updates.
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (!chartInstance) return;

    // On a full update ECharts replays the tooltip payload it captured before
    // the update (TooltipView._keepShow). replaceMerge renumbers the series, so
    // a replayed payload can name an index that no longer resolves and
    // getDataParams is then called on undefined. hideTip clears that captured
    // payload. Gate it on the series set actually changing: firing it on every
    // option change would also dismiss the tooltip on each zoom, since
    // zoomRange feeds echartOptions.
    //
    // Identity is the series id, not the name: an EV contributes one series or
    // two depending on whether it drove in the period on screen, and its two
    // strokes deliberately share a name so the legend treats them as one
    // device. Comparing names alone could miss one device gaining a dashed
    // twin as another loses one.
    const seriesKeys = (echartOptions.series || []).map(
      (entry) => `${entry?.id ?? entry?.name}`,
    );
    const previousKeys = previousSeriesNamesRef.current;
    const seriesSetChanged =
      !previousKeys ||
      previousKeys.length !== seriesKeys.length ||
      seriesKeys.some((key, index) => key !== previousKeys[index]);
    previousSeriesNamesRef.current = seriesKeys;

    if (seriesSetChanged) {
      chartInstance.dispatchAction({ type: "hideTip" });
    }

    /* A series-set change must be applied synchronously. lazyUpdate defers the
     * render but NOT the model: setOption returns with the new, shorter series
     * array already in place - and replaceMerge leaves a hole where a removed
     * series was, because ECharts keeps component slots by id. Any axis-pointer
     * activity in that window (the mouse is still over the chart) collects
     * against the stale coordinate system and hands the tooltip series indices
     * that no longer resolve. getSeriesByIndex is a raw array index - alone
     * among ECharts' series accessors it does not filter holes, unlike
     * getSeriesByName/getSeriesByType - so it returns undefined and
     * TooltipView._showAxisTooltip crashes on undefined.getDataParams.
     *
     * hideTip above cannot cover this: it clears the payload captured BEFORE
     * the update, while this one is collected after it. Closing the window is
     * the fix. Data-only updates - zoom, hover highlighting - keep the batching,
     * since nothing is removed and no index can go stale. */
    chartInstance.setOption(echartOptions, {
      replaceMerge: ["series"],
      lazyUpdate: !seriesSetChanged,
    });
  }, [echartOptions]);

  const suppressDeclarativeSetOption = useCallback(() => false, []);

  const displayedPowerAxisMax = useMemo(() => {
    const maxValue = axisExtents?.power?.max;
    return Number.isFinite(maxValue) ? maxValue.toFixed(1) : "";
  }, [axisExtents]);
  const displayedEffectivePowerAxisMax = useMemo(() => {
    const maxValue = effectivePowerAxisExtents?.max;
    return Number.isFinite(maxValue) ? maxValue.toFixed(1) : "";
  }, [effectivePowerAxisExtents]);
  /* Counted by legend entry rather than by series, so an EV drawn as a solid
   * and a dashed stroke still reads as the one highlighted thing it is. */
  const highlightedSeriesCount = useMemo(
    () =>
      new Set(series.filter((entry) => entry.z === 12).map((entry) => entry.name))
        .size,
    [series],
  );

  const handleDateChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (view === "daily") setSelDate(value);
      else if (view === "monthly") setSelMonth(value);
      else if (view === "yearly") setSelYear(value);
    },
    [view],
  );

  // Show error state with Retry button if data loading failed
  if (error) {
    return (
      <div
        className="error-container"
        style={{
          padding: "40px",
          textAlign: "center",
          background: "#fff5f5",
          border: "1px solid #feb2b2",
          borderRadius: "8px",
          margin: "20px 0",
        }}
      >
        <div style={{ fontWeight: 700, color: "#c53030", marginBottom: 8 }}>
          Failed to load simulation data
        </div>
        <div style={{ color: "#666", marginBottom: 16, whiteSpace: "pre-wrap" }}>
          {error}
        </div>
        <button
          type="button"
          onClick={() => refetchData?.()}
          style={{
            padding: "10px 24px",
            background: "#48bb78",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || !dailyData.length) {
    return (
      <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
        <Skeleton height={650} />
      </SkeletonTheme>
    );
  }

  return (
    <div className="chart-container-storage" data-testid="summary-overlay">
      <div className="summary-detail-sticky-header">
        {summaryHeader}
        <div className="overlay-toolbar">
        <div
          className="buttons-container"
          onMouseMove={handleLegendAreaHover}
          onMouseOver={handleLegendAreaHover}
          onMouseLeave={() => setHoveredLegendSeriesName("")}
        >
          <div className="btn-container-center">
            {displayedLegendItems.map((item) => {
              const isActive = isSeriesVisible(item);
              return (
                <div
                  className="btn-container"
                  key={item}
                  data-series-name={item}
                  onMouseEnter={() => setHoveredLegendSeriesName(item)}
                  onMouseOver={() => setHoveredLegendSeriesName(item)}
                  onMouseMove={() => setHoveredLegendSeriesName(item)}
                  onPointerEnter={() => setHoveredLegendSeriesName(item)}
                >
                  <button
                    data-testid={`overlay-toggle-${toTestId(item)}`}
                    data-series-name={item}
                    data-series-group={getSeriesGroup(item)}
                    aria-pressed={isActive}
                    className={`icon-btn ${isActive ? "active" : "inactive"}`}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => toggleSeries(item, event.currentTarget)}
                    onMouseEnter={() => setHoveredLegendSeriesName(item)}
                    onMouseOver={() => setHoveredLegendSeriesName(item)}
                    onMouseMove={() => setHoveredLegendSeriesName(item)}
                    onPointerEnter={() => setHoveredLegendSeriesName(item)}
                    style={{ borderColor: getSeriesColor(item) }}
                  >
                    <i
                      className={getIconClass(item)}
                      style={{ color: getSeriesColor(item) }}
                    ></i>
                  </button>
                  <span
                    data-testid={`overlay-label-${toTestId(item)}`}
                    className={`icon-span ${isActive ? "active" : "inactive"}`}
                    onMouseEnter={() => setHoveredLegendSeriesName(item)}
                    onMouseOver={() => setHoveredLegendSeriesName(item)}
                    onMouseMove={() => setHoveredLegendSeriesName(item)}
                    onPointerEnter={() => setHoveredLegendSeriesName(item)}
                    style={{ color: getSeriesColor(item) }}
                    title={item}
                  >
                    {item}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overlay-toolbar__header">
          <div className="controls-storage">
            <select
              data-testid="summary-overlay-view-select"
              onChange={(e) => setView(e.target.value)}
              value={view}
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="all time view">All Time View</option>
            </select>

            {view === "daily" && (
              <input
                data-testid="summary-overlay-period-input"
                type="date"
                onChange={handleDateChange}
                value={selDate}
                min={firstKey?.day || ""}
                max={lastKey?.day || ""}
              />
            )}
            {view === "monthly" && (
              <input
                data-testid="summary-overlay-period-input"
                type="month"
                onChange={handleDateChange}
                value={selMonth}
                min={firstKey?.month || ""}
                max={lastKey?.month || ""}
              />
            )}
            {view === "yearly" && (
              <select
                data-testid="summary-overlay-period-input"
                onChange={handleDateChange}
                value={selYear}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        </div>
      </div>

      <div
        ref={chartWrapperRef}
        className="chart-wrapper"
        data-testid="summary-overlay-chart"
        data-power-axis-max={displayedPowerAxisMax}
        data-effective-power-axis-max={displayedEffectivePowerAxisMax}
        data-storage-summary-series-max={
          seriesMaxByName[STORAGE_SUMMARY_SERIES_NAME] || ""
        }
        data-storage-summary-series-min={
          seriesMinByName[STORAGE_SUMMARY_SERIES_NAME] || ""
        }
        data-production-summary-series-max={
          seriesMaxByName[PRODUCTION_SUMMARY_SERIES_NAME] || ""
        }
        data-highlighted-summary-series={highlightedSeriesName}
        data-highlighted-series={highlightedSeriesName}
        data-highlighted-series-count={String(highlightedSeriesCount)}
        data-selected-period={selectedPeriod}
        data-selected-view={selectedPeriodType}
        data-power-axis-visible={isPricingOnlyView ? "false" : "true"}
        data-zoom-start={String(zoomRange.start)}
        data-zoom-end={String(zoomRange.end)}
      >
        {chartData.length > 0 ? (
          <ReactECharts
            ref={chartRef}
            option={echartOptions}
            onEvents={chartEvents}
            shouldSetOption={suppressDeclarativeSetOption}
            style={{ width: "100%", height: "100%" }}
            lazyUpdate={true}
            opts={{
              renderer: "canvas",
              devicePixelRatio: window.devicePixelRatio,
            }}
          />
        ) : (
          <div className="no-data-message">
            No data available for selected period
          </div>
        )}
      </div>
    </div>
  );
}
